/**
 * Collection & search listing behaviour for em-main-collection / em-main-search.
 *  - <em-collection>      filters drawer, sort, facet links and pagination rendered through the Section Rendering API
 *  - <em-price-range>     keeps the dual slider and the two number fields in sync
 *  - <em-floating-facets> shows the mobile filter button while the listing is on screen
 *  - <em-search-panel>    lazily loads one result type (products, articles, pages) into its tab
 */

const htmlCache = new Map();
let pendingRequest = null;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function sectionIdFor(element) {
  const host = element.closest('.shopify-section');
  return host ? host.id.replace('shopify-section-', '') : element.closest('[data-section-id]')?.getAttribute('data-section-id');
}

function withSectionId(url, sectionId) {
  const next = new URL(url, window.location.href);
  next.searchParams.set('section_id', sectionId);
  return next;
}

async function fetchSection(url, signal) {
  const key = url.toString();
  if (htmlCache.has(key)) return htmlCache.get(key);
  const response = await fetch(key, { signal, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Section request failed (${response.status})`);
  const html = await response.text();
  htmlCache.set(key, html);
  return html;
}

/**
 * Fetches the section for `url` and swaps it in place, keeping the filter groups the shopper had open.
 * @param {string} sectionId
 * @param {string | URL} url
 */
async function renderSection(sectionId, url) {
  const host = document.getElementById(`shopify-section-${sectionId}`);
  const pageUrl = new URL(url, window.location.href);
  pageUrl.searchParams.delete('section_id');
  pageUrl.hash = '';

  if (!host) {
    window.location.assign(pageUrl.toString());
    return;
  }

  pendingRequest?.abort();
  pendingRequest = new AbortController();

  const root = host.querySelector('em-collection');
  root?.setAttribute('aria-busy', 'true');
  history.replaceState(history.state, '', pageUrl.toString());

  const openGroups = new Set(
    Array.from(host.querySelectorAll('details.em-facet[open]'), (details) => details.getAttribute('data-em-filter'))
  );

  try {
    const html = await fetchSection(withSectionId(pageUrl, sectionId), pendingRequest.signal);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${sectionId}`) ?? doc.querySelector('.shopify-section');
    if (!fresh) throw new Error('Section markup missing');

    fresh.querySelectorAll('details.em-facet').forEach((details) => {
      details.toggleAttribute('open', openGroups.has(details.getAttribute('data-em-filter')));
    });

    const focusedId = document.activeElement instanceof HTMLElement ? document.activeElement.id : '';
    host.replaceChildren(...Array.from(fresh.childNodes, (node) => document.importNode(node, true)));

    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

    const anchor = host.querySelector('.em-collection__toolbar') ?? host.querySelector('.em-collection');
    requestAnimationFrame(() => {
      if (anchor && anchor.getBoundingClientRect().top < 0) {
        anchor.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    });
  } catch (error) {
    if (error?.name === 'AbortError') return;
    window.location.assign(pageUrl.toString());
  } finally {
    root?.removeAttribute('aria-busy');
  }
}

/** @param {HTMLFormElement} form */
function urlFromForm(form, { clear = false } = {}) {
  const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.href);
  url.search = '';

  for (const [key, value] of new FormData(form)) {
    if (typeof value !== 'string') continue;
    if (clear && !['q', 'type'].includes(key)) continue;
    if (value === '' && (key === 'page' || key.startsWith('filter.v.price'))) continue;
    url.searchParams.append(key, value);
  }

  return url;
}

class EmCollection extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const options = { signal: this.#abort.signal };
    this.addEventListener('click', this.#onClick, options);
    this.addEventListener('change', this.#onChange, options);
    this.addEventListener('submit', this.#onSubmit, { ...options, capture: true });
    this.addEventListener('reset', this.#onReset, { ...options, capture: true });
    this.addEventListener('keydown', this.#onKeydown, options);
    document.addEventListener('click', this.#closeMenus, options);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get sectionId() {
    return this.dataset.sectionId || sectionIdFor(this);
  }

  #drawerFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? /** @type {HTMLDialogElement | null} */ (document.getElementById(id)) : null;
  }

  #closeDrawers() {
    this.querySelectorAll('dialog.em-drawer[open]').forEach((dialog) => /** @type {HTMLDialogElement} */ (dialog).close());
  }

  #closeMenus = (event) => {
    this.querySelectorAll('details.em-sort-menu[open]').forEach((menu) => {
      if (!(event.target instanceof Node) || !menu.contains(event.target)) menu.removeAttribute('open');
    });
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-em-facets-open]');
    if (opener) {
      const drawer = this.#drawerFor(opener);
      if (drawer && !drawer.open) drawer.showModal();
      return;
    }

    if (target.closest('[data-em-drawer-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    if (target instanceof HTMLDialogElement && target.classList.contains('em-drawer')) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) target.close();
      return;
    }

    const tab = target.closest('[role="tab"]');
    if (tab) {
      this.#selectTab(tab);
      return;
    }

    const link = /** @type {HTMLAnchorElement | null} */ (target.closest('a[data-em-facet-link]'));
    if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      const url = new URL(link.href, window.location.href);
      if (link.hasAttribute('data-em-merge')) {
        const merged = new URLSearchParams(window.location.search);
        url.searchParams.forEach((value, key) => merged.set(key, value));
        merged.delete('page');
        url.search = merged.toString();
      }
      this.#closeDrawers();
      renderSection(this.sectionId, url);
    }
  };

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;

    if (input.name === 'mobile-items-per-row' || input.name === 'desktop-layout') {
      this.#saveLayout(input);
      return;
    }

    const form = input.form;
    if (!form || !form.classList.contains('em-facets')) return;

    if (form.hasAttribute('data-update-on-change')) {
      form.requestSubmit();
    } else {
      // Warm the cache so "Apply" feels instant.
      fetchSection(withSectionId(urlFromForm(form), this.sectionId)).catch(() => {});
    }
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('em-facets')) return;
    event.preventDefault();
    const url = urlFromForm(form);
    this.#closeDrawers();
    renderSection(this.sectionId, url);
  };

  #onReset = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('em-facets')) return;
    event.preventDefault();
    const url = urlFromForm(form, { clear: true });
    this.#closeDrawers();
    renderSection(this.sectionId, url);
  };

  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = Array.from(tab.parentElement?.querySelectorAll('[role="tab"]') ?? []);
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = tabs.length - 1;
    event.preventDefault();
    this.#selectTab(tabs[index]);
    /** @type {HTMLElement} */ (tabs[index]).focus();
  };

  #selectTab(tab) {
    const tabs = tab.parentElement?.querySelectorAll('[role="tab"]') ?? [];
    tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = document.getElementById(item.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !selected;
    });
  }

  #saveLayout(input) {
    const attributes =
      input.name === 'desktop-layout'
        ? { product_card_desktop_layout: input.value }
        : { product_card_mobile_items_per_row: Number(input.value) };

    const root = window.Shopify?.routes?.root ?? '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch(() => {});

    htmlCache.clear();
    const grid = this.querySelector('.em-collection__grid');
    if (!grid) return;
    if (input.name === 'desktop-layout') grid.setAttribute('data-desktop-layout', input.value);
    else grid.setAttribute('data-mobile-cols', input.value);
  }
}

class EmPriceRange extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const options = { signal: this.#abort.signal };
    this.addEventListener('input', this.#onInput, options);
    this.addEventListener('change', this.#onChange, options);
    this.addEventListener('focusin', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'number') event.target.select();
    }, options);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  #parts(bound) {
    return {
      slider: /** @type {HTMLInputElement} */ (this.querySelector(`input[type="range"][data-bound="${bound}"]`)),
      field: /** @type {HTMLInputElement} */ (this.querySelector(`input[type="number"][data-bound="${bound}"]`)),
    };
  }

  #clamp(bound, raw) {
    const min = this.#parts('min');
    const max = this.#parts('max');
    const ceiling = Number(max.slider.max) || 0;
    let value = Number.isFinite(raw) ? raw : bound === 'min' ? 0 : ceiling;
    if (bound === 'min') value = Math.max(0, Math.min(value, Number(max.field.value || ceiling) - 1));
    else value = Math.min(ceiling, Math.max(value, Number(min.field.value || 0) + 1));
    return value;
  }

  #paint() {
    const track = this.querySelector('.em-price-range__track');
    const ceiling = Number(this.#parts('max').slider.max) || 1;
    if (!(track instanceof HTMLElement)) return;
    track.style.setProperty('--em-range-from', `${(Number(this.#parts('min').slider.value) / ceiling) * 100}%`);
    track.style.setProperty('--em-range-to', `${(Number(this.#parts('max').slider.value) / ceiling) * 100}%`);
  }

  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'range') return;
    const bound = input.dataset.bound;
    const value = this.#clamp(bound, Number(input.value));
    input.value = String(value);
    this.#parts(bound).field.value = String(value);
    this.#paint();
  };

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const bound = input.dataset.bound;

    if (input.type === 'range') {
      // Forward the change to the named field so the form registers it.
      event.stopPropagation();
      const { field } = this.#parts(bound);
      field.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (input.value !== '') {
      const value = this.#clamp(bound, Number(input.value));
      input.value = String(value);
      this.#parts(bound).slider.value = String(value);
    }
    this.#paint();
  };
}

class EmFloatingFacets extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    window.addEventListener('scroll', this.#schedule, { passive: true });
    window.addEventListener('resize', this.#schedule, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#schedule);
    window.removeEventListener('resize', this.#schedule);
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const toolbar = this.closest('em-collection')?.querySelector('.em-collection__toolbar');
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    const pastToolbar = toolbar ? toolbar.getBoundingClientRect().bottom < 0 : false;
    const beforeFooter = footer ? footer.getBoundingClientRect().top > window.innerHeight : true;
    this.classList.toggle('is-visible', pastToolbar && beforeFooter);
  };
}

class EmSearchPanel extends HTMLElement {
  async connectedCallback() {
    const source = this.getAttribute('data-load-url');
    if (!source || this.dataset.loaded) return;
    this.dataset.loaded = 'true';

    try {
      const url = withSectionId(source, sectionIdFor(this));
      const html = await fetchSection(url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const panel = doc.getElementById(this.id);

      if (panel && panel.children.length) {
        this.replaceChildren(...Array.from(panel.childNodes, (node) => document.importNode(node, true)));
      } else {
        document.querySelector(`[aria-controls="${this.id}"]`)?.remove();
        this.remove();
      }
    } catch {
      // Leave the empty panel; the tab still links to the full results.
    }
  }
}

if (!customElements.get('em-collection')) customElements.define('em-collection', EmCollection);
if (!customElements.get('em-price-range')) customElements.define('em-price-range', EmPriceRange);
if (!customElements.get('em-floating-facets')) customElements.define('em-floating-facets', EmFloatingFacets);
if (!customElements.get('em-search-panel')) customElements.define('em-search-panel', EmSearchPanel);
